import { apiFetch, httpError } from '../services/apiFetch';

const API_URL = import.meta.env.VITE_API_URL;

export async function fetchQuotesTable(filter) {
    const body = {
        pageNumber: filter.pageNumber ?? 1,
        pageSize: filter.pageSize ?? 20,
        sortOptions: filter.sortOptions ?? null,
        searchOptions: filter.searchOptions ?? null,
        statusFilter: filter.statusFilter ?? null,
        // Set by the ViewCustomer page's scoped Quotes table. Null = no scope.
        customerId: filter.customerId ?? null,
    };

    const res = await apiFetch(`${API_URL}/api/Quote/GetQuotesTable`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await httpError(res, 'Failed to fetch quotes');
    return res.json();
}

export async function fetchQuoteById(id) {
    const res = await apiFetch(`${API_URL}/api/Quote/GetQuoteById/${id}`, { method: 'GET' });
    // Propagate 404 so the caller can render a "not found" panel — React
    // Query swallows the status code otherwise, so httpError attaches it.
    if (!res.ok) throw await httpError(res, 'Failed to fetch quote');
    return res.json();
}

export async function createQuote(body) {
    const res = await apiFetch(`${API_URL}/api/Quote/CreateQuote`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await httpError(res, 'Failed to create quote');
    return res.json();
}

export async function updateQuote(id, body) {
    const res = await apiFetch(`${API_URL}/api/Quote/UpdateQuote/${id}`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await httpError(res, 'Failed to update quote');
    return res.json();
}

export async function deleteQuote(id) {
    const res = await apiFetch(`${API_URL}/api/Quote/DeleteQuote/${id}`, {
        method: 'POST',
    });
    if (!res.ok) throw await httpError(res, 'Failed to delete quote');
    // 200 OK with no body.
}

export async function createQuoteLineItem(quoteId, body) {
    const res = await apiFetch(`${API_URL}/api/Quote/CreateQuoteLineItem/${quoteId}`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await httpError(res, 'Failed to add line item');
    return res.json();
}

export async function updateQuoteLineItem(id, body) {
    const res = await apiFetch(`${API_URL}/api/Quote/UpdateQuoteLineItem/${id}`, {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok) throw await httpError(res, 'Failed to update line item');
    return res.json();
}

export async function deleteQuoteLineItem(id) {
    const res = await apiFetch(`${API_URL}/api/Quote/DeleteQuoteLineItem/${id}`, {
        method: 'POST',
    });
    if (!res.ok) throw await httpError(res, 'Failed to delete line item');
}

// `body` captures the job-shape fields the operator fills in on the Convert
// modal (JobNumber / StartDate / EndDate / Site*). The server treats every
// field as optional — blank JobNumber triggers the "J-####" generator, blank
// StartDate defaults to today, everything else passes through as null.
export async function convertQuoteToJob(id, body) {
    const res = await apiFetch(`${API_URL}/api/Quote/ConvertToJob/${id}`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
    });
    if (!res.ok) throw await httpError(res, 'Failed to convert quote');
    return res.json();
}

export async function duplicateQuote(id, customerId) {
    const res = await apiFetch(`${API_URL}/api/Quote/DuplicateQuote/${id}`, {
        method: 'POST',
        body: JSON.stringify({ customerId }),
    });
    if (!res.ok) throw await httpError(res, 'Failed to duplicate quote');
    return res.json();
}

// Returns the raw Response so callers can pick the right consumer:
//   - `.blob()` for the download flow (→ object URL → anchor click)
//   - reused by the preview modal for the iframe src
// Using a shared Response keeps auth (Clerk bearer) applied via apiFetch — an
// iframe can't inject Authorization headers on its own.
export async function exportQuotePdf(id) {
    const res = await apiFetch(`${API_URL}/api/Quote/ExportPdf/${id}`, { method: 'GET' });
    if (!res.ok) throw await httpError(res, 'Failed to export quote');
    return res.blob();
}
